import type {ApiImageryCheckoutOrderedItem} from 'app/modules/Remote/api';
import * as conversionUtils from 'app/utils/conversionUtils';
import {formatCost} from 'app/utils/costUtils';
import {formatCaptureDate} from 'app/utils/imageryUtils';
import {getLayerKeyFromSourceAndLayer, getLayerResolution} from 'app/utils/layerUtils';

import {CartItemV1} from './types';

type PricedItem = Pick<CartItemV1, 'centsPerAcre' | 'billableAcres'>;

/** Matches the backend's line total: the per-acre price times the billed acres. */
export const itemPriceCents = (item: PricedItem): number => item.centsPerAcre * item.billableAcres;

export const cartTotalCents = (items: readonly PricedItem[]): number =>
  items.reduce((total, item) => total + itemPriceCents(item), 0);

export const cartTotalAcres = (items: CartItemV1[]): number =>
  items.reduce((total, item) => total + item.billableAcres, 0);

export const formatItemPrice = (item: PricedItem): string => formatCost(itemPriceCents(item));

/** The confirmation's line total. `priceCents` on an ordered item is per-acre, not the total. */
export const orderedItemPriceCents = (item: ApiImageryCheckoutOrderedItem): number =>
  item.priceCents * item.billedAcres;

export const formatOrderedItemPrice = (item: ApiImageryCheckoutOrderedItem): string =>
  formatCost(orderedItemPriceCents(item));

export const sceneCountLabel = (count: number): string =>
  count === 1 ? '1 scene' : `${count} scenes`;

/** Renders acreage the way OrderImageryModal does, so the same scene reads alike in both. */
export const formatAcresLabel = (acres: number): string =>
  `${conversionUtils.numberWithCommas(acres.toString())} acres`;

/** An order's total acreage, where "billed" separates what was charged from what a scene covers. */
export const formatBilledAcresLabel = (acres: number): string =>
  `${conversionUtils.numberWithCommas(acres.toString())} billed acres`;

export const formatPerAcrePrice = (centsPerAcre: number): string =>
  `${formatCost(centsPerAcre)}/acre`;

/** A cart item is a high-res truecolor scene; this is the layer key the property page shows it on. */
export const cartItemLayerKey = (sourceId: string): string =>
  getLayerKeyFromSourceAndLayer(sourceId, 'high-res-truecolor');

/** Renders the sensing date the way the property page does, so the same scene reads alike in both. */
export const formatSensingDate = (sensingTime: string, sourceId: string): string =>
  formatCaptureDate(sensingTime, cartItemLayerKey(sourceId), {showCaptureTime: true});

/** Source, resolution and capture date, as every cart surface lists them. */
export const sceneMetaParts = (item: CartItemV1): (string | null)[] => [
  item.snapshot.sourceDisplayName,
  resolutionLabel(item.sourceId),
  formatSensingDate(item.sensingTime, item.sourceId),
];

export const resolutionLabel = (sourceId: string): string | null => {
  const resolution: number | undefined = getLayerResolution(cartItemLayerKey(sourceId));
  return resolution == null ? null : `${resolution}m`;
};
/** The cap's one actionable sentence, shared by the order modal and the cart preview. */
export const cartFullMessage = (maxItems: number): string =>
  `Your cart is full at ${maxItems} scenes. Remove one to add another.`;
